// frontend/src/hooks/useParentDashboard.ts

import { useEffect, useState, useCallback, useRef } from 'react';
import { parentService } from '../services/parent.service';
import {
  ChildSummary,
  ChildOverview,
  ChildAIInsight,
  AiTopicsReport,
  ActivityReport,
  ReportPeriod,
} from '../types/parent.types';

const POLLING_INTERVAL_MS = 30_000; // 30 saniyede bir overview yenile

export function useParentDashboard() {
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [childrenLoading, setChildrenLoading] = useState(true);
  const [childrenError, setChildrenError] = useState<string | null>(null);

  const [overview, setOverview] = useState<ChildOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [insight, setInsight] = useState<string | null>(null);
  const [insightLoading, setInsightLoading] = useState(false);

  // --- Yeni state'ler ---
  const [activeTab, setActiveTab] = useState<ReportPeriod>('weekly');
  const [activityReport, setActivityReport] = useState<ActivityReport | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const [aiTopics, setAiTopics] = useState<AiTopicsReport | null>(null);
  const [aiTopicsLoading, setAiTopicsLoading] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Çocuk listesini yükle
  const reloadChildren = useCallback(async () => {
    setChildrenLoading(true);
    setChildrenError(null);
    try {
      const list = await parentService.listChildren();
      setChildren(list);
      if (list.length > 0 && !selectedChildId) {
        setSelectedChildId(list[0].id);
      }
    } catch (err) {
      setChildrenError(err instanceof Error ? err.message : 'Çocuk listesi yüklenemedi.');
    } finally {
      setChildrenLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reloadChildren();
  }, [reloadChildren]);

  // Overview yükle (sayfa açılışı + polling)
  const loadOverview = useCallback(async (childId: string) => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const data = await parentService.getChildOverview(childId);
      setOverview(data);
    } catch (err) {
      setOverviewError(err instanceof Error ? err.message : 'Özet yüklenemedi.');
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  // Aktivite raporunu yükle
  const loadActivityReport = useCallback(async (childId: string, period: ReportPeriod) => {
    setActivityLoading(true);
    try {
      const data = await parentService.getActivityReport(childId, period);
      setActivityReport(data);
    } catch {
      setActivityReport(null);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  // AI konularını yükle
  const loadAiTopics = useCallback(async (childId: string) => {
    setAiTopicsLoading(true);
    try {
      const data = await parentService.getAiTopics(childId);
      setAiTopics(data);
    } catch {
      setAiTopics(null);
    } finally {
      setAiTopicsLoading(false);
    }
  }, []);

  // Seçili çocuk değiştiğinde tüm verileri yenile
  useEffect(() => {
    if (!selectedChildId) return;

    setOverview(null);
    setInsight(null);
    setActivityReport(null);
    setAiTopics(null);

    loadOverview(selectedChildId);
    loadActivityReport(selectedChildId, activeTab);
    loadAiTopics(selectedChildId);

    // Polling kur
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => {
      loadOverview(selectedChildId);
    }, POLLING_INTERVAL_MS);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChildId]);

  // Tab değişiminde aktivite raporunu yenile
  useEffect(() => {
    if (!selectedChildId) return;
    loadActivityReport(selectedChildId, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // AI insight (manuel buton)
  const loadInsight = useCallback(() => {
    if (!selectedChildId || insightLoading) return;
    setInsightLoading(true);
    parentService
      .getChildAIInsight(selectedChildId)
      .then((data: ChildAIInsight) => {
        setInsight(data.insight);
      })
      .catch(() => {
        setInsight(null);
      })
      .finally(() => {
        setInsightLoading(false);
      });
  }, [selectedChildId, insightLoading]);

  return {
    children,
    childrenLoading,
    childrenError,
    selectedChildId,
    setSelectedChildId,
    overview,
    overviewLoading,
    overviewError,
    insight,
    insightLoading,
    loadInsight,
    reloadChildren,
    // --- Yeni ---
    activeTab,
    setActiveTab,
    activityReport,
    activityLoading,
    aiTopics,
    aiTopicsLoading,
  };
}
